import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  PersonaSummary,
  updatePersonaMetadata,
  deletePersonaRecord,
} from "components/AICompose/data/schema";
import CameraSVG from "assets/camera.svg";
import EditIcon from "assets/edit-ws.svg";
import TrashIcon from "assets/trash.svg";
import LikeIcon from "assets/like.svg";
import ShareIcon from "assets/share-link.svg";
import SearchIcon from "assets/search.svg";
import { showToast } from "components/ui-kit/Toast";

const getPersonaKey = (persona: PersonaSummary | null | undefined): string => {
  if (!persona) return "";
  if (persona.personaId && persona.personaId.trim()) return persona.personaId.trim();
  if (persona.taskId && persona.taskId.trim()) return persona.taskId.trim();
  return persona.name.trim();
};

type PersonaPickerModalProps = {
  open: boolean;
  personas: PersonaSummary[];
  selectedPersonaKey?: string | null;
  onSelect: (persona: PersonaSummary | null) => void;
  onClose: () => void;
};

export const PersonaPickerModal: React.FC<PersonaPickerModalProps> = ({
  open,
  personas,
  selectedPersonaKey,
  onSelect,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [editingDescription, setEditingDescription] = useState<string>("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const normalizedSelectedKey = (selectedPersonaKey || "").trim();
  const personaMap = useMemo(() => {
    const map = new Map<string, PersonaSummary>();
    personas.forEach((persona) => {
      map.set(getPersonaKey(persona), persona);
    });
    return map;
  }, [personas]);

  useEffect(() => {
    if (open && personas.length > 0) {
      console.log('[PersonaPickerModal] All personas from server:', personas.map(p => {
        const result: Record<string, any> = {
          id: p.id,
          name: p.name,
          personaId: p.personaId,
          key: getPersonaKey(p),
        };
        if (p.description) result.description = p.description;
        if (p.imageUrl) result.imageUrl = p.imageUrl;
        if (p.trackId) result.trackId = p.trackId;
        if (p.taskId) result.taskId = p.taskId;
        if (p.audioId) result.audioId = p.audioId;
        return result;
      }));
    }
  }, [open, personas]);

  useEffect(() => {
    if (!editingKey) return;
    const persona = personaMap.get(editingKey);
    if (!persona) {
      resetEditing();
      return;
    }
    setEditingName(persona.name);
    setEditingDescription(persona.description || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingKey, personaMap]);

  const filteredPersonas = useMemo(() => {
    if (!searchQuery.trim()) return personas;
    const query = searchQuery.toLowerCase().trim();
    return personas.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query))
    );
  }, [personas, searchQuery]);

  const toggleExpanded = (key: string) => {
    setExpandedKey((prev) => {
      const next = prev === key ? null : key;
      if (next !== key) {
        setEditingKey((current) => (current === key ? null : current));
      }
      return next;
    });
  };

  const beginEdit = (persona: PersonaSummary) => {
    const key = getPersonaKey(persona);
    setExpandedKey(key);
    setEditingKey(key);
    setEditingName(persona.name);
    setEditingDescription(persona.description || "");
  };

  const resetEditing = () => {
    setEditingKey(null);
    setEditingName("");
    setEditingDescription("");
    setSavingKey(null);
  };

  const handleSave = async (persona: PersonaSummary) => {
    const key = getPersonaKey(persona);
    const personaId = persona.id;
    if (!personaId) {
      console.log('[PersonaPicker] edit aborted: missing id', persona);
      showToast("Cannot update this persona yet. Try again later.", undefined, "error");
      return;
    }
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      showToast("Name cannot be empty.", undefined, "error");
      return;
    }

    setSavingKey(key);
    try {
      console.log('[PersonaPicker] updatePersonaMetadata', {
        personaId,
        suno_person_id: persona.personaId,
        name: trimmedName,
        description: editingDescription.trim(),
      });
      await updatePersonaMetadata(personaId, {
        name: trimmedName,
        description: editingDescription.trim(),
      });
      showToast("Persona updated.", undefined, "success");
      resetEditing();
    } catch (error) {
      console.error("Failed to update persona", error);
      showToast("Unable to update persona. Please try again.", undefined, "error");
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (persona: PersonaSummary) => {
    const key = getPersonaKey(persona);
    const personaId = persona.id;
    if (!personaId) {
      console.log('[PersonaPicker] delete aborted: missing id', persona);
      showToast("Cannot delete this persona yet. Try again later.", undefined, "error");
      return;
    }
    const confirmed = window.confirm(`Delete persona "${persona.name}"?`);
    if (!confirmed) return;

    setDeletingKey(key);
    try {
      console.log('[PersonaPicker] deletePersonaRecord', {
        personaId,
        suno_person_id: persona.personaId,
      });
      await deletePersonaRecord(personaId);
      showToast("Persona deleted.", undefined, "success");
      if (normalizedSelectedKey === key) {
        onSelect(null);
      }
      if (expandedKey === key) {
        setExpandedKey(null);
      }
      if (editingKey === key) {
        resetEditing();
      }
    } catch (error) {
      console.error("Failed to delete persona", error);
      showToast("Unable to delete persona. Please try again.", undefined, "error");
    } finally {
      setDeletingKey(null);
    }
  };

  const handleSelect = (persona: PersonaSummary | null) => {
    onSelect(persona);
    onClose();
  };

  const handleLike = (persona: PersonaSummary, event: React.MouseEvent) => {
    event.stopPropagation();
    // TODO: Implement like functionality
    showToast("Like functionality coming soon");
  };

  const handleShare = (persona: PersonaSummary, event: React.MouseEvent) => {
    event.stopPropagation();
    // TODO: Implement share functionality
    showToast("Share functionality coming soon");
  };

  if (!open) {
    return null;
  }

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={(event) => event.stopPropagation()}>
        <Header>
          <Title>Personas</Title>
          <CloseButton type="button" onClick={onClose}>
            ×
          </CloseButton>
        </Header>

        <SearchContainer>
          <SearchIconWrapper>
            <SearchIconSVG />
          </SearchIconWrapper>
          <SearchInput
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        {filteredPersonas.length ? (
          <PersonaList>
            {filteredPersonas.map((persona) => {
              const key = getPersonaKey(persona);
              const isSelected = key === normalizedSelectedKey;
              const isExpanded = expandedKey === key;
              const isEditing = editingKey === key;
              const isBusy = savingKey === key || deletingKey === key;
              const description = persona.description || "";
              const truncatedDescription = description.length > 60 
                ? `${description.substring(0, 60)}...` 
                : description;

              return (
                <PersonaItem key={`${persona.trackId || key}-${key}`} $selected={isSelected}>
                  <PersonaContent onClick={() => handleSelect(persona)}>
                    <PersonaAvatar>
                      {persona.imageUrl ? (
                        <AvatarImage src={persona.imageUrl} alt={persona.name} />
                      ) : (
                        <PersonaPlaceholderIcon />
                      )}
                    </PersonaAvatar>
                    <PersonaInfo>
                      <PersonaName>{persona.name}</PersonaName>
                      {description && (
                        <PersonaDescription>{truncatedDescription}</PersonaDescription>
                      )}
                    </PersonaInfo>
                  </PersonaContent>
                </PersonaItem>
              );
            })}
          </PersonaList>
        ) : (
          <EmptyState>
            {searchQuery ? "No personas found." : "No personas available yet."}
          </EmptyState>
        )}
      </Dialog>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12000;
  padding: 20px;
`;

const Dialog = styled.div`
  width: 480px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.16);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 80vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f4f4f4;
  color: #000;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &:hover {
    background: #e9ecef;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const SearchIconSVG = styled(SearchIcon)`
  width: 16px;
  height: 16px;
  stroke: #6c757d;
  fill: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #ffffff;
  color: #111;
  font-size: 14px;
  transition: border-color 0.2s ease;
  &:focus {
    outline: none;
    border-color: #6777ff;
  }
  &::placeholder {
    color: #6c757d;
  }
`;

const PersonaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
`;

const PersonaItem = styled.div<{ $selected?: boolean }>`
  border: 1px solid ${p => (p.$selected ? '#6777ff' : '#e9ecef')};
  background: ${p => (p.$selected ? '#f0f2ff' : '#ffffff')};
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color 0.2s ease, background 0.2s ease;
  position: relative;
`;

const PersonaContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  flex: 1;
`;

const PersonaAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8eaff, #f4f4f4);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(103, 119, 255, 0.25);
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PersonaPlaceholderIcon = styled(CameraSVG)`
  width: 24px;
  height: 24px;
  stroke: #6777ff;
  fill: none;
`;

const PersonaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const PersonaName = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: #111;
`;

const PersonaDescription = styled.span`
  font-size: 13px;
  color: #6c757d;
  line-height: 1.4;
`;

const PersonaActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
`;

const ActionIconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &:hover {
    background: #f4f4f4;
  }
`;

const LikeIconSVG = styled(LikeIcon)`
  width: 18px;
  height: 18px;
  stroke: #6c757d;
  fill: none;
`;

const ShareIconSVG = styled(ShareIcon)`
  width: 18px;
  height: 18px;
  stroke: #6c757d;
  fill: none;
`;

const MoreButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &:hover {
    background: #f4f4f4;
  }
`;

const ExpandedMenu = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 120px;
`;

const EditForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
`;

const FormLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #495057;
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #ffffff;
  color: #111;
  font-size: 14px;
  transition: border-color 0.2s ease;
  &:focus {
    outline: none;
    border-color: #6777ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormTextarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #ffffff;
  color: #111;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
  &:focus {
    outline: none;
    border-color: #6777ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SecondaryButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #e9ecef;
  background: #ffffff;
  color: #495057;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    background: #f4f4f4;
    border-color: #6777ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  padding: 8px 16px;
  border: none;
  background: #6777ff;
  color: #ffffff;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover:not(:disabled) {
    background: #5568e6;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e9ecef;
  border-top-color: #6777ff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 14px;
  padding: 40px 20px;
`;
